// Shared constants used across thumbnail editor and graphics features

export const JSON_KEYS = [
  "name",
  "gradientAngle",
  "selectable",
  "hasControls",
  "linkData",
  "editable",
  "extensionType",
  "extension",
  "templatePayload",
  "templatePayloadRanges",
  "groupValue",
];

export const FABRIC_CUSTOM_PROPS = [
  "templatePayload",
  "templatePayloadRanges",
  "name",
  "id",
  "selectable",
  "evented",
  "rx",
  "ry",
  "backgroundColor",
  "padding",
  "bgCornerRadius",
  "lockMovementX",
  "lockMovementY",
  "lockRotation",
  "lockScalingX",
  "lockScalingY",
  "lockSkewingX",
  "lockSkewingY",
  "_originalCenterX",
  "_originalCenterY",
  "_originalPlaceholderWidth",
  "_originalPlaceholderHeight",
  "groupValue",
  "_originalFontSize",
  "_originalWidth",
  "_originalHeight",
  "_originalTextLines",
  "_alignmentReference",
  "_scaleMultiplier",
  "_baseScaleX",
  "_baseScaleY",
  "_baseLeft",
  "_baseWidth",
  "_baseHeight",
  "_baseCenterY",
];

export const ELEMENT_TYPE = {
  TEXT: "text",
  IMAGE: "image",
  CUSTOM_IMAGE: "custom_image",
  CAPTURE_IMAGE: "capture_image",
  GROUP: "group",
};

export const filters = [
  "none",
  "polaroid",
  "sepia",
  "kodachrome",
  "contrast",
  "brightness",
  "greyscale",
  "brownie",
  "vintage",
  "technicolor",
  "pixelate",
  "invert",
  "blur",
  "sharpen",
  "emboss",
  "removecolor",
  "blacknwhite",
  "vibrance",
  "blendcolor",
  "huerotate",
  "resize",
  "saturation",
  "gamma",
];

export const fonts = [
  "Arial",
  "Arial Black",
  "Verdana",
  "Helvetica",
  "Tahoma",
  "Trebuchet MS",
  "Times New Roman",
  "Georgia",
  "Garamond",
  "Courier New",
  "Brush Script MT",
  "Palatino",
  "Bookman",
  "Comic Sans MS",
  "Impact",
  "Lucida Sans Unicode",
  "Geneva",
  "Lucida Console",
  "Adobe Caslon Pro",
  "Gotham",
  "Songti TC",
  "GT Walsheim",
  "Barlow Condensed",
];

export const EXTRA_LOCAL_FONTS = ["Adobe Caslon Pro", "Gotham", "GT Walsheim", "Barlow Condensed"];

export const selectionDependentTools = [
  "fill",
  "font",
  "filter",
  "opacity",
  "remove-bg",
  "stroke-color",
  "stroke-width",
];

export const FILL_COLOR = "rgba(0,0,0,1)";
export const STROKE_COLOR = "rgba(0,0,0,1)";
export const STROKE_WIDTH = 2;
export const STROKE_DASH_ARRAY = [];
export const FONT_FAMILY = "Arial";
export const FONT_SIZE = 32;
export const FONT_WEIGHT = 400;

export const CIRCLE_OPTIONS = {
  radius: 225,
  left: 100,
  top: 100,
  fill: FILL_COLOR,
  stroke: STROKE_COLOR,
  strokeWidth: STROKE_WIDTH,
};

export const RECTANGLE_OPTIONS = {
  left: 100,
  top: 100,
  fill: FILL_COLOR,
  stroke: STROKE_COLOR,
  strokeWidth: STROKE_WIDTH,
  width: 400,
  height: 400,
  angle: 0,
};

export const DIAMOND_OPTIONS = {
  left: 100,
  top: 100,
  fill: FILL_COLOR,
  stroke: STROKE_COLOR,
  strokeWidth: STROKE_WIDTH,
  width: 600,
  height: 600,
  angle: 0,
};

export const TRIANGLE_OPTIONS = {
  left: 100,
  top: 100,
  fill: FILL_COLOR,
  stroke: STROKE_COLOR,
  strokeWidth: STROKE_WIDTH,
  width: 400,
  height: 400,
  angle: 0,
};

export const TEXT_OPTIONS = {
  type: "textbox",
  left: 100,
  top: 100,
  fill: FILL_COLOR,
  fontSize: FONT_SIZE,
  fontFamily: FONT_FAMILY,
  lineHeight: 0.9,
  textWrap: true,
};

export const TEXTCASE_TOOL_OPTIONS = [
  {
    value: "default",
    label: "-",
  },
  {
    value: "capitalize",
    label: "Ag",
  },
  {
    value: "uppercase",
    label: "AG",
  },
  {
    value: "lowercase",
    label: "ag",
  },
];

export const POSITION_TOOL_OPTIONS = [
  {
    value: "top_left",
    label: "Top Left",
  },
  {
    value: "top_center",
    label: "Top Center",
  },
  {
    value: "top_right",
    label: "Top Right",
  },
  {
    value: "center_left",
    label: "Center Left",
  },
  {
    value: "center_center",
    label: "Center",
  },
  {
    value: "center_right",
    label: "Center Right",
  },
  {
    value: "bottom_left",
    label: "Bottom Left",
  },
  {
    value: "bottom_center",
    label: "Bottom Center",
  },
  {
    value: "bottom_right",
    label: "Bottom Right",
  },
];

export const TEXT_FONT_WEIGHT_OPTIONS = [
  {
    value: 100,
    label: "Thin",
  },
  {
    value: 200,
    label: "Extra Light",
  },
  {
    value: 300,
    label: "Light",
  },
  {
    value: 400,
    label: "Regular",
  },
  {
    value: 500,
    label: "Medium",
  },
  {
    value: 600,
    label: "Semi Bold",
  },
  {
    value: 700,
    label: "Bold",
  },
  {
    value: 800,
    label: "Extra Bold",
  },
  {
    value: 900,
    label: "Black",
  },
];

export const TEMPLATE_PAYLOAD = {
  [ELEMENT_TYPE.CUSTOM_IMAGE]: {
    type: ELEMENT_TYPE.CUSTOM_IMAGE,
    key: ELEMENT_TYPE.CUSTOM_IMAGE,
    elementType: ELEMENT_TYPE.IMAGE,
    alignment: "center",
  },
  [ELEMENT_TYPE.CAPTURE_IMAGE]: {
    type: ELEMENT_TYPE.CAPTURE_IMAGE,
    key: ELEMENT_TYPE.CAPTURE_IMAGE,
    elementType: ELEMENT_TYPE.IMAGE,
    alignment: "center",
  },
  [ELEMENT_TYPE.GROUP]: {
    key: "free_group",
    type: "free_group",
    elementType: ELEMENT_TYPE.GROUP,
    alignment: "center",
    maxRows: 1,
    maxCols: 1,
    spacing: 10,
  },
};

export const GROUP_OPTIONS = [
  {
    value: "free_group",
    label: "Free Group",
  },
  {
    value: "game",
    label: "Game",
  },
  {
    value: "stats_item",
    label: "Stats Item",
  },
  {
    value: "stats_group",
    label: "Stats Group",
  },
];

export const GROUP_ALIGNMENT_OPTIONS = [
  {
    value: "center",
    label: "Center",
  },
  {
    value: "left",
    label: "Left",
  },
  {
    value: "right",
    label: "Right",
  },
];

export const EXPORT_FORMAT_OPTIONS = [
  {
    value: "png",
    label: "PNG",
  },
  {
    value: "jpg",
    label: "JPG",
  },
];

export const DATE_FORMAT_OPTIONS = [
  // === BASIC DATE ONLY ===
  { label: "2025-10-19", value: "YYYY-MM-DD" },
  { label: "19/10/2025", value: "DD/MM/YYYY" },
  { label: "10/19/2025", value: "MM/DD/YYYY" },
  { label: "19 Oct 2025", value: "DD MMM YYYY" },
  { label: "Oct 19 2025", value: "MMM DD YYYY" },
  { label: "October 19, 2025", value: "MMMM DD, YYYY" },
  { label: "19th October 2025", value: "Do MMMM YYYY" },
  { label: "19 October", value: "DD MMMM" },
  { label: "October 19", value: "MMMM DD" },
  { label: "Oct 19", value: "MMM DD" },
  { label: "19 Oct", value: "DD MMM" },
  { label: "Oct 2025", value: "MMM YYYY" },
  { label: "October 2025", value: "MMMM YYYY" },
  { label: "’25", value: "YY" },
  { label: "2025", value: "YYYY" },

  // === TIME ONLY (common in Insta/Twitter posts) ===
  { label: "2:45 PM", value: "h:mm A" },
  { label: "2:45:30 PM", value: "h:mm:ss A" },
  { label: "14:45", value: "HH:mm" },
  { label: "14:45:30", value: "HH:mm:ss" },
  { label: "2 PM", value: "h A" },
  { label: "2:45 pm", value: "h:mm a" },

  { label: "Sunday", value: "dddd" },
  { label: "Sun", value: "ddd" },
];

export const PLAYER_NAME_REGEX = /^player_\d+_name$/;
